import { useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useAppSelector } from '@shared/store';
import {
  type Privilege,
  useCampaignControllerCampaignQuery,
  useCampaignAgentListControllerListQuery,
  useUserMeControllerUserMeQuery,
  useToolListControllerCampaignToolListQuery,
} from '@shared/services/apiService';
import KnowledgeBaseTab from '@pages/Admin/Campaign/ui/SidebarTabs/KnowledgeBaseTab';
import McpToolsSection from '@pages/Admin/Campaign/ui/SidebarTabs/components/McpToolsSection';
import CampaignWebhookSection from '@pages/Admin/Campaign/ui/SidebarTabs/components/CampaignWebhookSection';
import PostCallMessagesTab from '@pages/Admin/Campaign/ui/SidebarTabs/PostCallMessagesTab';
import SipTrunkTab from '@pages/Admin/Campaign/ui/SidebarTabs/SipTrunkTab';
import OngoingCallsTab from '@pages/Admin/Campaign/ui/SidebarTabs/OngoingCallsTab';
import CallAnalysisStatistics from '@pages/Admin/Campaign/ui/SidebarTabs/analysis/CallAnalysisStatistics';
import type { WorkspaceModule } from '@shared/designVariants/catalog';
import { useAskPageContext } from '@shared/designVariants/useAskPageContext';
import { AudienceWorkspace } from './AudienceWorkspace';
import { AgentToolsWorkspace } from './AgentToolsWorkspace';
import { EvaluationWorkspace } from './EvaluationWorkspace';

export const CampaignResources = ({
  module,
  campaignId,
  onDirtyChange,
  requestNavigation,
  contextSurface,
}: {
  module: WorkspaceModule;
  campaignId: string;
  onDirtyChange: (value: boolean) => void;
  requestNavigation: (action: () => void) => void;
  contextSurface?: WorkspaceModule;
}) => {
  const {
    currentData: campaign,
    isFetching,
    isError,
    refetch,
  } = useCampaignControllerCampaignQuery({ id: campaignId });
  const {
    currentData: directAgents,
    isError: agentError,
    isFetching: agentsFetching,
  } = useCampaignAgentListControllerListQuery({ id: campaignId });
  const {
    currentData: tools = [],
    isError: toolError,
    refetch: refetchTools,
  } = useToolListControllerCampaignToolListQuery(
    { id: campaignId },
    { skip: !['Tools', 'Connections'].includes(module) },
  );
  const { data: me } = useUserMeControllerUserMeQuery();
  const user = useAppSelector((state) => state.auth.authenticatedUser);
  const company = useAppSelector((state) => state.auth.authenticatedUserCompany);
  const [connectionTab, setConnectionTab] = useState(0);
  useAskPageContext(
    contextSurface && campaign && !isError && campaign.companyId === user?.companyId
      ? {
          page: module,
          campaign: { id: campaign.id, title: campaign.title },
        }
      : null,
    contextSurface ?? null,
  );
  if (isFetching && !campaign) return <CircularProgress aria-label="Loading campaign" />;
  if (isError || !campaign || campaign.companyId !== user?.companyId)
    return (
      <Alert severity="error" action={<Button onClick={() => void refetch()}>Retry</Button>}>
        Campaign is unavailable in the current company.
      </Alert>
    );
  const viewer = user?.role === 'viewer';
  const allowed = (privilege: Privilege) => !viewer && !!me?.privileges.includes(privilege);
  const locked = campaign.status !== 'inactive';
  const agents = campaign.squad?.agents ?? directAgents ?? [];
  const knowledge = (
    <KnowledgeBaseTab
      campaignId={campaign.id}
      agents={agents}
      isSquadCampaign={!!campaign.squad}
      userRole={allowed('knowledge-base.manage') ? user?.role : 'viewer'}
    />
  );
  const mcp =
    toolError || agentError ? (
      <Alert severity="error">Unable to load current tools or agents.</Alert>
    ) : (
      <McpToolsSection
        campaignId={campaign.id}
        agents={agents}
        tools={tools}
        refetch={refetchTools}
        isCampaignActive={locked || !allowed('agent.manage')}
      />
    );
  const webhook = (
    <CampaignWebhookSection
      campaignId={campaign.id}
      isDisabled={locked || !allowed('webhook.manage')}
    />
  );
  switch (module) {
    case 'Knowledge base':
      return me?.privileges.includes('knowledge-base.read') ? (
        knowledge
      ) : (
        <Alert severity="warning">Knowledge base read permission is required.</Alert>
      );
    case 'Tools':
      return (
        <Stack gap={3}>
          {mcp}
          <AgentToolsWorkspace
            campaign={campaign}
            agents={agents}
            tools={tools}
            locked={locked || !allowed('agent.manage')}
            refetch={refetch}
          />
        </Stack>
      );
    case 'Webhooks':
      return webhook;
    case 'WhatsApp':
      return (
        <PostCallMessagesTab
          campaignId={campaign.id}
          campaignName={campaign.title}
          companyName={company?.title || ''}
          readOnly={!allowed('post-call-message.manage')}
        />
      );
    case 'SIP trunks':
    case 'Phone numbers':
      return (
        <SipTrunkTab
          readOnly={!allowed('sip-trunk.manage')}
          campaign={campaign}
          onDirtyChange={onDirtyChange}
        />
      );
    case 'Audiences':
      return (
        <AudienceWorkspace
          campaign={campaign}
          locked={locked || !allowed('campaign.manage')}
          onDirtyChange={onDirtyChange}
        />
      );
    case 'Call history':
      return (
        <Box sx={{ minHeight: 500, height: '70vh' }}>
          <OngoingCallsTab
            campaignId={campaign.id}
            campaignData={campaign}
            campaignType={campaign.type}
          />
        </Box>
      );
    case 'Analytics':
      return <CallAnalysisStatistics campaign={campaign} />;
    case 'Evaluations':
      return (
        <EvaluationWorkspace
          campaign={campaign}
          agents={agents}
          onDirtyChange={onDirtyChange}
          canTest={allowed('call.test')}
          readOnly={!allowed('structured-output.manage')}
          agentsUnavailable={!campaign.squad && (agentError || agentsFetching || !directAgents)}
        />
      );
    case 'Connections':
      return (
        <Stack gap={2}>
          <Tabs
            value={connectionTab}
            onChange={(_, value) =>
              requestNavigation(() => {
                onDirtyChange(false);
                setConnectionTab(value);
              })
            }
            variant="scrollable"
            scrollButtons="auto">
            <Tab label="Tools and MCP" />
            <Tab label="SIP" />
            <Tab label="Webhooks" />
            <Tab label="WhatsApp" />
          </Tabs>
          {connectionTab === 0 ? (
            mcp
          ) : connectionTab === 1 ? (
            <SipTrunkTab
              readOnly={!allowed('sip-trunk.manage')}
              campaign={campaign}
              onDirtyChange={onDirtyChange}
            />
          ) : connectionTab === 2 ? (
            webhook
          ) : (
            <PostCallMessagesTab
              campaignId={campaign.id}
              campaignName={campaign.title}
              companyName={company?.title || ''}
              readOnly={!allowed('post-call-message.manage')}
            />
          )}
        </Stack>
      );
    default:
      return <Typography>Select a supported campaign resource.</Typography>;
  }
};
